import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Subject, Subscription, catchError, defer, filter, fromEvent, interval, map, take, takeUntil, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { IBedState, IBedStateHistory, IBedStatesSnapshot, IBedStateUpdate, canViewBedStates, compareBedStates, isBedState } from '../../shared/models/be/bed-state';
import { MonitoringPreferencesService } from './monitoring-preferences.service';
import { SharedService } from './shared.service';
import { RequestsService } from './utils/requests.service';

interface BedStatesScope { key: string; buildingId: string; unitIds: string[]; }

@Injectable({ providedIn: 'root' })
export class BedStatesService {
  private readonly http = inject(HttpClient);
  private readonly requests = inject(RequestsService);
  private readonly preferences = inject(MonitoringPreferencesService);
  private readonly shared = inject(SharedService);
  private session?: Subscription;
  private pending?: Subscription;
  private consumers = 0;
  private generation = 0;
  private scope: BedStatesScope | null = null;
  private buffered: IBedStateUpdate[] = [];
  private readonly scopeInvalidated = new Subject<void>();
  readonly invalidated$ = this.scopeInvalidated.asObservable();
  private serverOffset = 0;
  readonly states = signal<ReadonlyMap<string, IBedState>>(new Map());
  readonly loading = signal(false);
  readonly error = signal('');
  readonly denied = signal(false);
  readonly now = signal(Date.now());

  get canView(): boolean { return canViewBedStates(this.requests.getCurrentUser()); }

  /** Only superuser views acquire the session. All rooms share one bulk request. */
  acquire(): () => void {
    if (!this.canView) return () => {};
    if (++this.consumers === 1) {
      this.session = new Subscription();
      this.session.add(this.requests.currentUser$.subscribe(() => this.synchronizeScope()));
      this.session.add(this.preferences.invalidated$.subscribe(() => this.changeScope(null)));
      this.session.add(this.preferences.snapshots$.subscribe(() => this.synchronizeScope()));
      this.session.add(this.shared.connected$.subscribe(() => this.refresh()));
      this.session.add(this.shared.bedStateUpdates$.subscribe(update => this.receive(update)));
      this.session.add(fromEvent(document, 'visibilitychange').pipe(filter(() => document.visibilityState === 'visible'))
        .subscribe(() => this.refresh()));
      this.session.add(fromEvent(window, 'online').subscribe(() => this.refresh()));
      this.session.add(interval(1000).subscribe(() => this.now.set(Date.now() + this.serverOffset)));
      this.synchronizeScope();
    }
    let released = false;
    return () => {
      if (released) return;
      released = true;
      if (--this.consumers === 0) {
        this.session?.unsubscribe();
        this.session = undefined;
        this.changeScope(null);
      }
    };
  }

  refresh(): void {
    if (!this.scope || !this.isCurrent(this.scope)) return;
    this.pending?.unsubscribe();
    const scope = this.scope, generation = ++this.generation;
    this.buffered = [];
    this.loading.set(true);
    this.error.set('');
    this.denied.set(false);
    // Omitting unitIds asks the BE for every selected unit (including > 50 units).
    this.pending = this.http.get<IBedStatesSnapshot>(`${environment.apiEndpoint}/bed-states`, {
      params: { buildingId: scope.buildingId }
    }).pipe(take(1), map(snapshot => {
      if (snapshot.buildingId !== scope.buildingId || !Number.isFinite(Date.parse(snapshot.asOf))
        || !Array.isArray(snapshot.items) || !snapshot.items.every(isBedState)) throw new Error('Invalid bed states snapshot');
      return snapshot;
    })).subscribe({
      next: snapshot => {
        if (generation !== this.generation || !this.isCurrent(scope)) return;
        const next = new Map<string, IBedState>();
        for (const bed of snapshot.items) {
          if (!this.includes(bed, scope)) continue;
          const current = this.states().get(bed.logicalBedId);
          next.set(bed.logicalBedId, current && compareBedStates(current, bed) > 0 ? current : bed);
        }
        this.states.set(next);
        this.serverOffset = Date.parse(snapshot.asOf) - Date.now();
        this.now.set(Date.now() + this.serverOffset);
        this.loading.set(false);
        const buffered = this.buffered;
        this.buffered = [];
        for (const update of buffered) {
          if (Date.parse(update.emittedAt) > Date.parse(snapshot.asOf)) this.apply(update.bed);
        }
      },
      error: (error: unknown) => {
        if (generation !== this.generation || !this.isCurrent(scope)) return;
        this.loading.set(false);
        if (error instanceof HttpErrorResponse && [401, 403].includes(error.status)) this.deny();
        else {
          this.error.set('BED_STATES.LOAD_ERROR');
          for (const update of this.buffered) this.apply(update.bed);
        }
        this.buffered = [];
      }
    });
  }

  history$(bed: IBedState, from: string, to: string) {
    return defer(() => {
      const scope = this.scope;
      if (!scope || !this.isCurrent(scope) || this.denied() || !this.includes(bed, scope)) {
        return throwError(() => new HttpErrorResponse({ status: 403 }));
      }
      return this.http.get<IBedStateHistory>(`${environment.apiEndpoint}/bed-states/${encodeURIComponent(bed.deviceId)}/history`, {
        params: { buildingId: scope.buildingId, from, to }
      }).pipe(take(1), takeUntil(this.scopeInvalidated), filter(() => this.isCurrent(scope)), catchError(error => {
        if (error instanceof HttpErrorResponse && [401, 403].includes(error.status)) this.deny();
        return throwError(() => error);
      }));
    });
  }

  private currentScope(): BedStatesScope | null {
    const user = this.requests.getCurrentUser(), selection = this.preferences.selection();
    if (!canViewBedStates(user) || !user?.building?.id || selection?.state !== 'READY'
      || selection.buildingId !== user.building.id || !selection.selectedUnitIds.length) return null;
    const unitIds = [...selection.selectedUnitIds].sort();
    return { buildingId: selection.buildingId, unitIds,
      key: JSON.stringify([user.id, user.role, user.customer?.id, user.organization?.id, selection.buildingId,
        selection.version, this.preferences.revision(), unitIds]) };
  }

  private isCurrent(scope: BedStatesScope): boolean { return this.currentScope()?.key === scope.key; }

  private synchronizeScope(): void {
    const next = this.currentScope();
    if (next?.key !== this.scope?.key) {
      this.changeScope(next);
      if (next) this.refresh();
    }
  }

  private changeScope(next: BedStatesScope | null): void {
    ++this.generation;
    this.pending?.unsubscribe();
    this.pending = undefined;
    this.scopeInvalidated.next();
    this.scope = next;
    this.buffered = [];
    this.states.set(new Map());
    this.loading.set(false);
    this.error.set('');
    this.denied.set(false);
    this.serverOffset = 0;
  }

  private includes(bed: IBedState, scope: BedStatesScope): boolean {
    return bed.buildingId === scope.buildingId && scope.unitIds.includes(bed.unitId);
  }

  private receive(update: IBedStateUpdate): void {
    if (!this.scope || !this.isCurrent(this.scope) || this.denied() || update.buildingId !== this.scope.buildingId
      || !isBedState(update.bed) || !Number.isFinite(Date.parse(update.emittedAt)) || !this.includes(update.bed, this.scope)) return;
    if (this.loading()) this.buffered.push(update);
    else this.apply(update.bed);
  }

  private apply(bed: IBedState): void {
    const current = this.states().get(bed.logicalBedId);
    if (current && compareBedStates(bed, current) < 0) return;
    this.states.update(states => new Map(states).set(bed.logicalBedId, bed));
  }

  private deny(): void {
    this.denied.set(true);
    this.states.set(new Map());
    this.error.set('BED_STATES.FORBIDDEN');
  }
}
